import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, setPortal, UNAUTHENTICATED_EVENT } from '../api/client';
import type { Me, Portal } from '../api/types';

interface Session {
  me: Me | null;
  loading: boolean;
  portal: Portal | null;
  setPortalCode: (code: string) => void;
  /** Resolves `{ sentTo }` when the browser must be verified first (P-07, ON-06a), else null. */
  signIn: (email: string, password: string) => Promise<{ sentTo: string } | null>;
  signOut: () => Promise<void>;
  /** ON-06 — re-fetches identity after a forced password change clears
   *  `mustChangePassword`, so the app can move on without a full reload. */
  refreshMe: () => Promise<void>;
  /**
   * SA-01 in the UI. Super Admin is `true` for everything, never a list lookup —
   * the same rule as the server guard, for the same reason: a list is right today
   * and wrong the day a permission is added.
   */
  can: (permission: string) => boolean;
  /** Does the current portal offer this capability? Distinct from `can`. */
  offers: (capability: string) => boolean;
}

const SessionContext = createContext<Session | null>(null);

const PORTAL_KEY = 'miraki.portal';

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalCode, setCode] = useState<string | null>(
    () => { try { return localStorage.getItem(PORTAL_KEY); } catch { return null; } },
  );

  // A 401 from any call means the session is over — the API renews the access
  // token itself while the refresh cookie lives, so this fires only when it does
  // not. Dropping `me` routes the whole app to sign-in, which beats each screen
  // rendering its own refusal.
  useEffect(() => {
    // The cache goes with the session: whoever signs in next on this tab must not
    // first paint the previous user's figures.
    const end = () => { setMe(null); queryClient.clear(); };
    window.addEventListener(UNAUTHENTICATED_EVENT, end);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, end);
  }, [queryClient]);

  useEffect(() => {
    let alive = true;
    api.get<Me>('/auth/me', { noPortal: true })
      .then((m) => { if (alive) setMe(m); })
      .catch((e) => { if (alive && !(e instanceof ApiError && e.isUnauthenticated)) console.error(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // The chosen portal must be one this user actually holds. A code left in
  // localStorage from another account would otherwise send an x-portal header
  // the server refuses, and every screen would fail for no visible reason.
  const portal = useMemo(() => {
    if (!me) return null;
    // A retailer has exactly one portal — their account's — and no switcher.
    // Their scope is not a choice they get to make.
    if (me.kind === 'retailer') return me.portal;
    if (me.portals.length === 0) return null;
    return me.portals.find((p) => p.code === portalCode) ?? me.portals[0] ?? null;
  }, [me, portalCode]);

  // Covers the paths the setter does not: first load, and a stored code that
  // resolves to a different portal than the one requested.
  useEffect(() => { setPortal(portal?.code ?? null); }, [portal]);

  const value: Session = {
    me,
    loading,
    portal,
    setPortalCode: (code) => {
      // Synchronously, not only via the effect below. React Query refetches as
      // soon as the key changes, and an effect is not guaranteed to have run by
      // then — so the first request after a switch could still carry the old
      // header while the key already said the new portal.
      setPortal(code);
      setCode(code);
      try { localStorage.setItem(PORTAL_KEY, code); } catch { /* private mode */ }
    },
    signIn: async (email, password) => {
      /*
       * A correct password is no longer necessarily a session. ON-06a: on a browser
       * this account has not used, the server returns a challenge instead and the
       * code goes to the user's email. `me` must NOT be fetched in that case — there
       * is nothing to fetch, and asking would read as a failed sign-in.
       */
      const r = await api.post<{ deviceVerificationRequired?: boolean; sentTo?: string }>(
        '/auth/login', { email, password }, { noPortal: true });
      if (r.deviceVerificationRequired) return { sentTo: r.sentTo ?? '' };
      setMe(await api.get<Me>('/auth/me', { noPortal: true }));
      return null;
    },
    signOut: async () => {
      await api.post('/auth/logout', undefined, { noPortal: true }).catch(() => {});
      setMe(null);
      // Privacy: drop every cached response so the next user on this tab starts empty.
      queryClient.clear();
    },
    refreshMe: async () => {
      setMe(await api.get<Me>('/auth/me', { noPortal: true }));
    },
    // Permissions are an internal concept. A retailer holds none — their
    // authority is bounded by owning the account, not by a catalog — so `can`
    // is false for them rather than throwing.
    can: (permission) => {
      if (!me || me.kind !== 'internal') return false;
      const i = me;
      return i.isSuperAdmin || i.permissions.includes(permission);
    },
    offers: (capability) => !!portal?.capabilities?.[capability],
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Exported beside the provider on purpose. Fast Refresh prefers a module to
 * export components alone, and splitting this into its own file to satisfy that
 * would separate the hook from the context it reads — the one pairing that has
 * to stay together for `useSession outside SessionProvider` to mean anything.
 * The cost is a full reload when this file changes, which is rare.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
